import { z } from 'zod';
import { PAYMENT_RECIPIENTS } from '../../constants.js';
import { COMPLETION_SOURCE } from '../../types/lender/events.js';
import { LenderIdSchema } from '../../types/lender/general.js';
import { IS_DEALER, SVP_IDS } from './shared-fields.js';

/**
 * Action-item events—transitions observed from the action-item lifecycle and
 * tracked to the CDP by `trackActionItemEvent`, the generic route that sits on
 * the `action-item.event` Pub/Sub consumer.
 *
 * NAMING—READ BEFORE ADDING AN ENTRY. This file deliberately breaks the
 * registry's own rule. Convention §2 says a canonical names what happened in
 * Ekho's domain and never the mechanism that carried it, which would make the
 * first insurance ask something like `insurance_proof_requested`. Entries here
 * are instead named mechanically, `{actionCode}_{status}` lowercased, matching
 * `NotificationEvent` byte for byte.
 *
 * That is a considered trade, not an oversight:
 *
 * 1. `NotificationEvent` is already the shared vocabulary for exactly these
 *    transitions. Ops and lifecycle marketing read those strings today, and
 *    Customer.io is taking over the sends those strings key. One name per
 *    transition across both systems keeps the migration auditable—every
 *    template has one obvious Segment event, and a double-send during the
 *    cutover is visible rather than buried under a translation layer.
 * 2. The mechanical rule needs no judgement per action item, so the route
 *    generalises to the other ~130 codes without a naming debate each time.
 * 3. A broader event taxonomy is not settled. Inventing domain names now is
 *    the expensive-to-reverse direction; this one is cheap to reverse, because
 *    `wireName` exists for precisely this. If the taxonomy later lands on
 *    `insurance_proof_requested`, that becomes `canonical` and the string
 *    below moves to `wireName`, with no discontinuity in the warehouse.
 *
 * The cost, accepted knowingly: `EKHO_EVENTS` now holds two naming systems, so
 * these sit in BigQuery beside domain-named events like `insurance_verified`.
 * Do not copy this file's convention into a group that is not mirroring an
 * existing action-item registry.
 *
 * PII: CDP-only, ids and bounded enums, with two documented exceptions.
 * `surveyUrl` carries the buyer's email inside its Refiner contact params. That
 * adds no exposure the CDP does not already hold (it is the same address
 * Customer.io sends to). `buyerName` is on the shared envelope, because the
 * dealer-facing templates have to name whose order is waiting and the recipient
 * already sees that name in the admin portal. Both are permitted by convention
 * §9 only because nothing here declares `destinations` — adding an ad-fanout
 * destination to an action-item event would ship buyer PII to Meta or TikTok,
 * so that is the check to run before widening the pattern.
 *
 * `buyerPortalUrl` is the dealer's public buyer-portal `/login` URL, derived at
 * track time — not a one-time token. Credentialed magic links still belong at
 * send time; do not mint them onto these events (Customer.io drips reuse the
 * trigger payload).
 *
 * `adminPortalUrl` is its dealer-side counterpart, and passes the same test for
 * the same reason: an order deep link assembled from stable ids, with no token
 * to expire between the first send and the third reminder. The two are the only
 * URLs on these events, and any third one must clear that bar before it lands
 * here rather than at send time.
 *
 * Schemas cover caller-provided properties only. Backend identity/attribution
 * fields are injected by `ServerAnalytics` per call and stay out of these
 * schemas.
 */
const PENDING_KEY_SUFFIX = '_PENDING';
const DISMISSED_KEY_SUFFIX = '_DISMISSED';
/**
 * Context every action-item event carries, so each one joins back to the order
 * and the vehicle it concerns (convention §12). Ids resolve from
 * `actionItem.metadata`. `buyerPortalUrl` is derived at track time from
 * `domainsServicesProviderId` (a public `/login` URL, not a credential).
 */
const actionItemContext = {
    orderId: z.string().describe('Placed order this action item belongs to.'),
    purchasedVehicleId: z.string().describe('Purchased vehicle this action item belongs to.'),
    vehicleName: z
        .string()
        .describe('Buyer-facing vehicle label used in email copy: make, model, and year (e.g. "Apex Trail 350 HP 2026").'),
    vin: z
        .string()
        .optional()
        .describe('Vehicle identification number. Omitted when the vehicle has none assigned yet.'),
    programOwnerId: z.string().describe('Program owner tenant the order belongs to.'),
    buyerName: z
        .string()
        .optional()
        .describe("Buyer's full name, for dealer-facing copy that has to say whose order needs the work. Read from the item's `buyerFullName` metadata, which both generator families stamp. Absent when the generator recorded no name — the financing generator writes `''` for a nameless buyer, and that is dropped rather than shipped as an empty string. The one piece of buyer PII on these events, and it stays inside the CDP: these are CDP-only, so no ad platform receives it. Buyer-addressed events carry it too, where it names the recipient rather than a third party."),
    buyerPortalUrl: z
        .string()
        .optional()
        .describe('Dealer buyer-portal (or shop) host plus `/login`, matching the CTA the old Ekho buyer emails minted at send time. Public login URL, not a magic link. Absent when `domainsServicesProviderId` is missing or `not_set`, and when `getDomainUrl` throws.'),
    adminPortalUrl: z
        .string()
        .describe("Admin-portal CTA for dealer-facing copy, replacing the per-send `actionUrl` those templates used to link to. An order deep link when the domain owner and order both resolve, otherwise the environment's portal base URL. Not a credential and not per-send: built from stable ids so Customer.io drip reminders replay safely. Required rather than optional because the base URL always resolves. Lands on buyer-facing events too, where nothing reads it — cheaper than a per-event opt-in list."),
    ...SVP_IDS,
    ...IS_DEALER,
    lenderId: LenderIdSchema.optional().describe("Lender the action item belongs to: the item's `lenderId` metadata, else its `bankName`, which holds the lender id on every lender item. Absent on items not tied to a lender. An order can have several lenders' items open at once, so a journey matches its exit event on orderId and lenderId together."),
    bankName: z
        .string()
        .optional()
        .describe("The item's `bankName` metadata as stored. It equals lenderId on every lender item, but the field is due to be renamed and the two can diverge, so match journeys on lenderId. Absent on items not tied to a lender."),
};
/**
 * Shared action-item envelope without the backend base props.
 *
 * `programOwnerId` and `domainOwnerSvpId` are declared on the backend surface.
 * Restating them on an entry shadows those declarations, so the composed
 * payload uses the surface versions. The envelope builder still sends both.
 */
const { programOwnerId: _programOwnerId, domainOwnerSvpId: _domainOwnerSvpId, ...actionItemContextWithoutBaseProps } = actionItemContext;
/**
 * Context for an action item that hangs off the placed order rather than a
 * purchased vehicle. `generatePlacedOrderActionItemMetadata` stamps no vehicle
 * keys, so these fields are filled at track time from the order's vehicles
 * rather than from the item. They stay optional: present when the order has
 * exactly one purchased vehicle, omitted when it has zero or more than one,
 * and omitted on a lookup failure (never empty strings).
 */
const placedOrderScopedContext = {
    ...actionItemContext,
    purchasedVehicleId: z
        .string()
        .optional()
        .describe('Sole purchased vehicle on this order. Absent when the order has zero or more than one vehicle, and when the lookup fails.'),
    vehicleName: z
        .string()
        .optional()
        .describe('Buyer-facing make/model/year of the sole vehicle on this order. Absent when the order has zero or more than one vehicle, and when the lookup fails.'),
    vin: z
        .string()
        .optional()
        .describe('VIN of the sole vehicle on this order. Absent when that vehicle has none assigned, when the order is not a single-vehicle order, and when the lookup fails.'),
};
/**
 * The parties an internal ops Slack alert names in its own copy.
 *
 * Held out of `actionItemContext` on purpose. The 48 events already registered
 * here drive buyer-facing EMAIL, and Customer.io reads a recipient's own name
 * off their profile rather than off the event, so none of them carries these
 * three. The events that drive an internal SLACK post are different: the
 * message body names the buyer, the selling dealer and the OEM to an ops reader
 * who is none of them, so a profile attribute cannot supply it. Spreading it
 * here rather than repeating it nineteen times, and rather than promoting it
 * onto the shared envelope, which would widen all 48 email payloads for no
 * consumer.
 *
 * PII tiering (convention §9): these are CDP-only events with no ad
 * destinations, where PII is permitted given a stated need. The stated need is
 * that today's Slack message already renders `buyerFullName`, and an ops alert
 * that cannot name the deal it concerns is not actionable.
 *
 * All three are optional because each has a real absent case, and absence is
 * expressed by omission rather than a sentinel (convention §11) - notably NOT
 * the literal `'N/A'` that `interpolateTemplate` renders today.
 */
const slackAlertParties = {
    buyerFullName: z
        .string()
        .optional()
        .describe("Buyer's full name, so an ops reader can identify the deal the alert concerns. Stamped from the order by the shared action-item metadata builders. Absent when the order carries no buyer name."),
    sellerName: z
        .string()
        .optional()
        .describe('Display name of the services provider selling the vehicle. Resolved from the order\'s SALE provider. Absent when the order has no SALE provider, and when that lookup fails - the legacy Slack path rendered the literal "N/A" in both cases.'),
    oemId: z
        .string()
        .optional()
        .describe('OEM tenant the vehicle belongs to. Absent on orders whose metadata builder did not resolve one; the placed-order builder stamps it conditionally.'),
};
/** What Customer.io routes a Record funding approval event on, sent on every deal. */
const recordFundingApprovalProps = {
    lenderName: z
        .string()
        .optional()
        .describe('Display name of the lender that approved funding. Absent when the item stamped none.'),
    fundsRecipient: z
        .enum([PAYMENT_RECIPIENTS.DEALER, PAYMENT_RECIPIENTS.EKHO])
        .optional()
        .describe("Who the lender pays, from the deal's funds-recipient stamp. Absent when the deal carries no stamp."),
    dealerSold: z
        .boolean()
        .describe('A dealer, not Ekho, sells the vehicle, so the dealer records the funding.'),
};
/**
 * The fulfillment statuses a vehicle can hold, for the Zod enum below. A known
 * value set is an enum, never free text (convention §7).
 *
 * Listed literally, with `VehicleFulfillmentStatus` imported as a TYPE only.
 * Importing the const object instead would pull
 * `types/purchasedVehicles/PurchasedVehicle` into this module at runtime and
 * close a dependency cycle back through `utils/util_funcs`, which the bundler
 * reports. A type-only import erases, so it cannot.
 *
 * Drift is a compile error in both directions: `satisfies` rejects a value that
 * is not a real status, and `Covered` fails to resolve if a status is ever
 * added upstream without being added here.
 */
const VEHICLE_FULFILLMENT_STATUS_VALUES = [
    'UNFULFILLED',
    'FULFILLED',
    'READY_TO_FULFILL',
    'CANCELED',
    'ON_HOLD',
    'IN_TRANSIT_TO_BUYER',
    'IN_TRANSIT_TO_FULFILLMENT_CENTER',
    'RECEIVED_AT_FULFILLMENT_CENTER',
    'PICKUP_SCHEDULED',
    'READY_TO_DISPATCH',
];
/**
 * Delivery timing, which is what makes a counter-signature alert urgent rather
 * than merely informational: the ops reader needs to know how long they have.
 * Stamped by the shared purchased-vehicle metadata builder.
 */
const signingDeadline = {
    estimatedDeliveryDate: z
        .string()
        .optional()
        .describe('Estimated delivery date for the vehicle, as the metadata builder formatted it for display. Absent when the order has no delivery estimate yet.'),
    daysUntilDelivery: z
        .number()
        .optional()
        .describe('Days from now until the estimated delivery date, so an ops reader can triage without doing the arithmetic. Absent when there is no delivery estimate. Can be negative on an overdue order.'),
};
/**
 * Which post-delivery form a re-upload transition is about. Several re-upload
 * items can be live for one vehicle at once — the generator mints one per
 * rejected form, keyed by `iterationKey` — so these fields are what let a
 * consumer bind an ask to its own completion instead of the next form's. The
 * risk is concentrated in COMPLETE: without them a journey waiting on form A's
 * re-upload takes form B's completion as its own exit signal.
 *
 * `formKey` and `iteration` together reconstruct that `iterationKey`, which is a
 * top-level field on the action item rather than metadata and so cannot be read
 * off the event directly. `formDisplayName` is the human label for the same form
 * and is what the email renders; it is not a stable key, so match on `formKey`.
 */
const postFulfillmentFormIdentity = {
    formKey: z
        .string()
        .describe('Stable identifier of the form being re-requested, as the state form selection stored it (e.g. `tireForm`). The field to match on when binding an ask to its completion. Not normalized to one casing across states — match the exact key, never a lowercased guess.'),
    iteration: z
        .number()
        .describe('Which round of review this form is on, counting from the first submission. Increments each time the form is rejected again.'),
    formDisplayName: z
        .string()
        .describe('Human-readable form name for email copy (e.g. "Odometer Disclosure"). Falls back to `formKey` when the form carries no display name, so it is always present. Display only — not stable enough to key on.'),
};
/** Shared describe copy for a counter-signer's signing-vendor URL field. */
const SELLER_SIGNATURE_URL_DESCRIPTION = 'Signing-vendor URL the counter-signer opens to sign. Absent until the vendor has issued one.';
/**
 * Context for a sales-agent inbox item.
 *
 * These items hang off a lead, not an order, so the order and vehicle slots
 * on the shared envelope are optional. The lead generators stamp none of
 * them, and the envelope omits a field it cannot see.
 *
 * `leadUrl` is the call to action: a stable admin-portal path built from the
 * services provider id and the lead id, safe for a Customer.io reminder to
 * replay. `adminPortalUrl` on the same payload is the portal base, because
 * the envelope deep-links only the document-review codes.
 *
 * `buyerName` is the envelope's name for metadata `buyerFullName`. These
 * events do not also carry `buyerFullName`, an email, or a phone number.
 */
const agentRequestContext = {
    ...actionItemContext,
    orderId: z
        .string()
        .optional()
        .describe('Placed order, when the lead item happens to carry one. Omitted on the AgentRequest and reply items, which are not order-scoped.'),
    purchasedVehicleId: z
        .string()
        .optional()
        .describe('Purchased vehicle, when the item carries one. Omitted on AgentRequest and reply items.'),
    vehicleName: z
        .string()
        .optional()
        .describe('Buyer-facing vehicle label, when the item carries one. Omitted on AgentRequest and reply items.'),
    programOwnerId: z
        .string()
        .optional()
        .describe('Program owner tenant. Read from item metadata when that holds a real id, otherwise from the action item itself. Omitted when neither is a real id.'),
    leadUrl: z
        .string()
        .describe('Admin-portal URL for this lead. Built from the services provider id and the lead id, with no token, so a reminder can replay it. The dealer email call to action.'),
    conversationId: z.string().describe('Conversation the request or the owed reply belongs to.'),
    requestId: z
        .string()
        .optional()
        .describe('AgentRequest id. Present on answer, value, and media items, where it is also the iteration key. Absent on reply-to-conversation, which is keyed by the conversation.'),
    question: z
        .string()
        .optional()
        .describe('The question the sales agent asked the dealership. Present on answer, value, and media items when the request has one. Absent on reply-to-conversation, and omitted when the stored question is blank.'),
    mediaKind: z
        .enum(['image', 'video', 'document'])
        .optional()
        .describe('Kind of file the sales agent asked for. Present on submit-media items. Absent on the other three, and omitted when the request stored none.'),
};
const agentRequestEventSchema = z.object(agentRequestContext);
/**
 * Scheduling details for a partnered at-home VIN verification.
 *
 * All three are optional. A served county stamps a vendor name and URL. An
 * eligible item that is missing one omits that key rather than sending an
 * empty string. `titleOnly` selects the titling line versus the
 * title-and-registration line, and is omitted when the generator did not
 * stamp `reg31TitleOnly`.
 */
const vinVerificationScheduling = {
    vinVerificationVendorName: z
        .string()
        .optional()
        .describe("Display name of the partnered vendor who can verify the VIN at the buyer's home. Absent when the generator did not stamp one."),
    vinVerificationSchedulingUrl: z
        .string()
        .optional()
        .describe("The vendor's scheduling page. A stable public URL, not a one-time token, so a Customer.io reminder can replay it. Absent when the generator did not stamp one."),
    titleOnly: z
        .boolean()
        .optional()
        .describe('True when the order is titling with no registration, which selects the titling line of the scheduling message. False when the order is title and registration. Absent when the generator did not stamp `reg31TitleOnly`.'),
};
const ACTION_ITEM_EVENTS = {
    // The buyer is being asked for proof of insurance.
    // Dedupe: once per submission cycle. A rejection opens a new cycle (a fresh
    // `insurance.submissionId`), which re-fires this with `attempt` incremented
    // rather than introducing a second "re-requested" event—a repeat is not a new
    // verb (convention §4).
    SUBMIT_INSURANCE_INFORMATION_PENDING: {
        canonical: 'submit_insurance_information_pending',
        surface: 'backend',
        description: 'Ekho asked the buyer to provide proof of insurance.',
        schema: z.object({
            ...actionItemContext,
            attempt: z
                .number()
                .describe('Which ask this is: 1 on the first request, incrementing each time a rejection opens a new submission cycle.'),
            priorRejectionReason: z
                .string()
                .optional()
                .describe("The operator's note explaining why the PREVIOUS cycle was closed and the buyer asked again, so the re-ask can say what to fix. Names the cycle before this one, not this one — `attempt` says which. Carries the note from any of the three closing paths: a failed doc review, a failed provider verification, or a manual re-request. Absent on `attempt` 1, whenever the operator left no note, and on cycles closed before the note was retained."),
            insuranceRequirement: z
                .enum(['required', 'optional'])
                .optional()
                .describe('`required` when the order needs a policy, `optional` when it needs none but offers one to buy. Customer.io routes `optional` to the single optional-worded email. Absent on items generated before this field existed.'),
        }),
    },
    // The buyer uploaded proof. Null notification template — no Ekho email —
    // but Customer.io wait-for-submission steps key this transition. Dedupe:
    // once per submission cycle, on a genuine COMPLETE, not born-complete history.
    SUBMIT_INSURANCE_INFORMATION_COMPLETE: {
        canonical: 'submit_insurance_information_complete',
        surface: 'backend',
        description: 'The buyer submitted proof of insurance.',
        schema: z.object({
            ...actionItemContext,
            attempt: z
                .number()
                .describe('Which ask this submission answers: 1 on the first request, incrementing each time a rejection opens a new submission cycle.'),
        }),
    },
    // Both outcomes ride this one name, discriminated by `result`, because both
    // are the same action-item transition. An approval completes the live cycle's
    // review item; a rejection rotates the cycle into `priorSubmissions`, where
    // the generator re-emits that same item as COMPLETE history, completing the
    // same Firestore doc. COMPLETE therefore means "this review cycle closed",
    // never "approved" — the outcome is stamped into the item's metadata by
    // `describeReviewOutcome` and read from there.
    //
    // This is where the 1:1 with `NotificationEvent` is loosest: over there the
    // same string means approval only, since rejection has its own re-request
    // notification. Anything triggering on this event must branch on `result`
    // rather than assume the notification-side meaning.
    REVIEW_INSURANCE_INFORMATION_COMPLETE: {
        canonical: 'review_insurance_information_complete',
        surface: 'backend',
        description: "Ekho finished reviewing the buyer's submitted insurance and reached an outcome.",
        schema: z.object({
            ...actionItemContext,
            result: z
                .enum(['approved', 'rejected'])
                .describe("The reviewer's decision on the submitted policy."),
            rejectionReason: z
                .string()
                .optional()
                .describe('Why the policy was rejected. Absent when `result` is `approved`, and when the reviewer supplied no note.'),
            reviewedBy: z
                .string()
                .optional()
                .describe('Operator email that reviewed the policy, so an ops reader can see who cleared it. Absent on an auto-decided cycle, which has no human reviewer.'),
        }),
    },
    // Placed-order-scoped. The action item completes for every non-pending review
    // status; the tracker emits only on reviewer approval, matching the in-code
    // buyer email. The name itself means "cleared": no result discriminant.
    REVIEW_RISK_ASSESSMENT_COMPLETE: {
        canonical: 'review_risk_assessment_complete',
        surface: 'backend',
        description: 'Ekho approved the order risk assessment and the buyer can continue.',
        schema: z.object(placedOrderScopedContext),
    },
    // The buyer has not yet handed Ekho a credit application. The generator reads
    // the applicant SSN as the proxy for "submitted", so this is PENDING from the
    // moment the vehicle is set to native financing until that lands.
    SUBMIT_FINANCING_APPLICATION_TO_EKHO_PENDING: {
        canonical: 'submit_financing_application_to_ekho_pending',
        surface: 'backend',
        description: 'Ekho asked the buyer to submit their financing application.',
        schema: z.object(actionItemContext),
    },
    SUBMIT_FINANCING_APPLICATION_TO_EKHO_COMPLETE: {
        canonical: 'submit_financing_application_to_ekho_complete',
        surface: 'backend',
        description: 'The buyer submitted their financing application to Ekho.',
        schema: z.object(actionItemContext),
    },
    // Approved and rejected lender decisions are separate action codes, not one
    // code with an outcome field, so neither needs the `result` discriminant the
    // insurance review carries. COMPLETE here means the buyer opened the surfaced
    // decision, not that they accepted or declined the offer — that is
    // `DECLINE_FINANCING_OFFER` and the down-payment item downstream.
    REVIEW_SURFACED_LENDER_DECISION_APPROVED_PENDING: {
        canonical: 'review_surfaced_lender_decision_approved_pending',
        surface: 'backend',
        description: "Ekho surfaced an approved lender decision for the buyer's review.",
        schema: z.object(actionItemContext),
    },
    REVIEW_SURFACED_LENDER_DECISION_APPROVED_COMPLETE: {
        canonical: 'review_surfaced_lender_decision_approved_complete',
        surface: 'backend',
        description: 'The buyer reviewed the approved lender decision.',
        schema: z.object(actionItemContext),
    },
    REVIEW_SURFACED_LENDER_DECISION_REJECTED_PENDING: {
        canonical: 'review_surfaced_lender_decision_rejected_pending',
        surface: 'backend',
        description: "Ekho surfaced a rejected lender decision for the buyer's review.",
        schema: z.object(actionItemContext),
    },
    REVIEW_SURFACED_LENDER_DECISION_REJECTED_COMPLETE: {
        canonical: 'review_surfaced_lender_decision_rejected_complete',
        surface: 'backend',
        description: 'The buyer reviewed the rejected lender decision.',
        schema: z.object(actionItemContext),
    },
    // Cleared and not-cleared stipulation decisions are separate action codes, so
    // the outcome is in the event name and neither needs the `result` discriminant
    // the insurance review carries. COMPLETE means the buyer opened the surfaced
    // decision — for the not-cleared case, what follows is a fresh evidence ask,
    // not an acceptance.
    REVIEW_SURFACED_LENDER_DECISION_STIPS_FULLY_CLEARED_PENDING: {
        canonical: 'review_surfaced_lender_decision_stips_fully_cleared_pending',
        surface: 'backend',
        description: "Ekho surfaced a lender decision clearing every stipulation for the buyer's review.",
        schema: z.object(actionItemContext),
    },
    REVIEW_SURFACED_LENDER_DECISION_STIPS_FULLY_CLEARED_COMPLETE: {
        canonical: 'review_surfaced_lender_decision_stips_fully_cleared_complete',
        surface: 'backend',
        description: 'The buyer reviewed the lender decision clearing every stipulation.',
        schema: z.object(actionItemContext),
    },
    REVIEW_SURFACED_LENDER_DECISION_STIPS_NOT_CLEARED_PENDING: {
        canonical: 'review_surfaced_lender_decision_stips_not_cleared_pending',
        surface: 'backend',
        description: "Ekho surfaced a lender decision leaving stipulations uncleared for the buyer's review.",
        schema: z.object(actionItemContext),
    },
    REVIEW_SURFACED_LENDER_DECISION_STIPS_NOT_CLEARED_COMPLETE: {
        canonical: 'review_surfaced_lender_decision_stips_not_cleared_complete',
        surface: 'backend',
        description: 'The buyer reviewed the lender decision leaving stipulations uncleared.',
        schema: z.object(actionItemContext),
    },
    // New stipulations after the buyer has already accepted that lender.
    // COMPLETE is the buyer acknowledging the stipulations modal, which can
    // happen before they upload the new evidence. Envelope only.
    REVIEW_SURFACED_LENDER_DECISION_STIPS_NEW_PENDING: {
        canonical: 'review_surfaced_lender_decision_stips_new_pending',
        surface: 'backend',
        description: "Ekho surfaced a lender request for new stipulations for the buyer's review.",
        schema: z.object(actionItemContext),
    },
    REVIEW_SURFACED_LENDER_DECISION_STIPS_NEW_COMPLETE: {
        canonical: 'review_surfaced_lender_decision_stips_new_complete',
        surface: 'backend',
        description: 'The buyer reviewed the lender request for new stipulations.',
        schema: z.object(actionItemContext),
    },
    SUBMIT_STIPULATION_EVIDENCE_PENDING: {
        canonical: 'submit_stipulation_evidence_pending',
        surface: 'backend',
        description: 'Ekho asked the buyer for evidence to clear a lender stipulation.',
        schema: z.object(actionItemContext),
    },
    // Emitted on every completion, unlike the buyer email and the ops Slack
    // alert, which both fire only on a recorded buyer submission. The item also
    // completes when the stipulations were already validated, when Ekho never
    // asked the buyer, and when the order funds — and a journey waiting on this
    // is waiting to stop asking, which all four routes satisfy. Inheriting that
    // suppression would strand buyers in reminders for an ask that no longer
    // exists.
    //
    // `buyerProvidedEvidence` carries the distinction the name alone loses, so
    // read the completion as "the ask closed" and branch on the flag for "the
    // buyer uploaded something". Same shape of trade as
    // `REVIEW_INSURANCE_INFORMATION_COMPLETE` above, and the same warning applies:
    // the notification-side meaning is narrower than this event's.
    //
    // The ops Slack alert is a second emission of this same event, attributed to
    // Ekho, and only when the flag is true. That copy also carries the parties
    // block, `bankName`, and `stipulations` the Slack template interpolates.
    SUBMIT_STIPULATION_EVIDENCE_COMPLETE: {
        canonical: 'submit_stipulation_evidence_complete',
        surface: 'backend',
        description: 'The ask for stipulation evidence closed.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            buyerProvidedEvidence: z
                .boolean()
                .describe('Whether the buyer uploaded evidence for at least one stipulation. False when the ask closed without them: stipulations already validated, no request made of the buyer, or the order funded.'),
            bankName: z
                .string()
                .optional()
                .describe('Lender that raised the stipulations. Absent when the item stamped none.'),
            stipulations: z
                .array(z.string())
                .optional()
                .describe('Stipulation keys the buyer provided evidence for, as the generator stored them. Keys rather than rendered labels, so a copy change cannot break a filter. Absent when the item stamped none.'),
        }),
    },
    // Only asked once the buyer has accepted an offer; before that the item sits
    // QUEUED and this never fires.
    PAY_DOWN_PAYMENT_PENDING: {
        canonical: 'pay_down_payment_pending',
        surface: 'backend',
        description: 'Ekho asked the buyer to pay the down payment on their accepted financing offer.',
        schema: z.object(actionItemContext),
    },
    // "Owes nothing further", reached four ways: no down payment was required,
    // earlier payments already cover it, or the payment is processing or
    // succeeded. All four mean the same thing to a journey waiting on this, so
    // the event carries no discriminant between them.
    PAY_DOWN_PAYMENT_COMPLETE: {
        canonical: 'pay_down_payment_complete',
        surface: 'backend',
        description: "The buyer's down payment obligation was satisfied.",
        schema: z.object(actionItemContext),
    },
    // Every lender on the active offer rejected the buyer and the buyer has
    // acknowledged it. The order auto-cancels 72 hours after this item goes
    // pending, unless the buyer pays in full. Envelope only: the journey
    // matches on orderId.
    CANCEL_ORDER_AFTER_FINAL_REJECTION_PENDING: {
        canonical: 'cancel_order_after_final_rejection_pending',
        surface: 'backend',
        description: 'Every lender rejected the buyer, the buyer has seen it, and the order will cancel unless they pay in full.',
        schema: z.object(actionItemContext),
    },
    // The order was canceled after that rejection. An item created already
    // complete stays silent, so a backfill of a canceled order does not emit.
    CANCEL_ORDER_AFTER_FINAL_REJECTION_COMPLETE: {
        canonical: 'cancel_order_after_final_rejection_complete',
        surface: 'backend',
        description: 'The order was canceled after every lender rejected the buyer.',
        schema: z.object(actionItemContext),
    },
    // The buyer-side half of the cash purchase. Ekho's own confirmation is
    // `RECEIVE_FULL_PAYMENT`, which waits for a settled receipt; this one also
    // completes while the payment is still processing, so it can land first.
    // No PENDING counterpart is mapped: no journey triggers on the ask.
    PAY_IN_FULL_COMPLETE: {
        canonical: 'pay_in_full_complete',
        surface: 'backend',
        description: 'The buyer paid for the vehicle in full.',
        schema: z.object(actionItemContext),
    },
    // Placed-order-scoped, like the risk review. Asked only once titling and
    // registration is the active section on some vehicle in the order, so a buyer
    // who verifies earlier goes straight to COMPLETE without ever being asked.
    SUBMIT_IDENTITY_VERIFICATION_PENDING: {
        canonical: 'submit_identity_verification_pending',
        surface: 'backend',
        description: 'Ekho asked the buyer to verify their identity.',
        schema: z.object(placedOrderScopedContext),
    },
    SUBMIT_IDENTITY_VERIFICATION_COMPLETE: {
        canonical: 'submit_identity_verification_complete',
        surface: 'backend',
        description: "The buyer's identity was verified.",
        schema: z.object(placedOrderScopedContext),
    },
    // Titling and registration details the buyer alone can supply: plate
    // category, use type, owner type. Only generated where Ekho runs the titling
    // process; a dealer-handled vehicle produces no item and so no event.
    SUBMIT_OWNER_DETAILS_PENDING: {
        canonical: 'submit_owner_details_pending',
        surface: 'backend',
        description: 'Ekho asked the buyer for the owner details needed to title the vehicle.',
        schema: z.object(actionItemContext),
    },
    SUBMIT_OWNER_DETAILS_COMPLETE: {
        canonical: 'submit_owner_details_complete',
        surface: 'backend',
        description: 'The buyer supplied the owner details needed to title the vehicle.',
        schema: z.object(actionItemContext),
    },
    // The cash path's sale contract. Cash vs financed is settled by the action
    // code, not a property: the generator returns no item here for a natively
    // financed vehicle, which gets `SIGN_LOAN_CONTRACT` instead.
    //
    // The one place `vin` is reliably absent. This goes PENDING on payment plus
    // the agreement being out for signature, both upstream of `ASSIGN_VIN`, so
    // the optional field is the norm here rather than the exception.
    SIGN_PURCHASE_AGREEMENT_PENDING: {
        canonical: 'sign_purchase_agreement_pending',
        surface: 'backend',
        description: 'Ekho asked the buyer to sign their purchase agreement.',
        schema: z.object(actionItemContext),
    },
    SIGN_PURCHASE_AGREEMENT_COMPLETE: {
        canonical: 'sign_purchase_agreement_complete',
        surface: 'backend',
        description: 'The buyer signed their purchase agreement.',
        schema: z.object(actionItemContext),
    },
    // Bill of sale, asked of every buyer regardless of how they are paying.
    SIGN_SALE_DOCUMENT_PENDING: {
        canonical: 'sign_sale_document_pending',
        surface: 'backend',
        description: 'Ekho asked the buyer to sign their bill of sale.',
        schema: z.object(actionItemContext),
    },
    SIGN_SALE_DOCUMENT_COMPLETE: {
        canonical: 'sign_sale_document_complete',
        surface: 'backend',
        description: 'The buyer signed their bill of sale.',
        schema: z.object(actionItemContext),
    },
    // Retail Installment Contract, the financed counterpart to the purchase
    // agreement above. Only reachable once a lender offer has been accepted.
    SIGN_LOAN_CONTRACT_PENDING: {
        canonical: 'sign_loan_contract_pending',
        surface: 'backend',
        description: 'Ekho asked the buyer to sign their Retail Installment Contract.',
        schema: z.object(actionItemContext),
    },
    SIGN_LOAN_CONTRACT_COMPLETE: {
        canonical: 'sign_loan_contract_complete',
        surface: 'backend',
        description: 'The buyer signed their Retail Installment Contract.',
        schema: z.object(actionItemContext),
    },
    GENERATE_OFF_PLATFORM_FINANCING_CONTRACT_PENDING: {
        canonical: 'generate_off_platform_financing_contract_pending',
        surface: 'backend',
        description: "Someone was asked to generate the off-platform loan contract: ops for an adapter lender, the seller when the contract is signed in the lender's own system. One event for every off-platform lender. lenderName is the display name.",
        schema: z.object({
            ...actionItemContext,
            lenderName: z
                .string()
                .optional()
                .describe('Display name of the off-platform lender, such as Octane or Ironhorse Funding.'),
            financingContractOutsideEkho: z
                .boolean()
                .optional()
                .describe("True when the contract is signed in the lender's own system, so the event tracks on the seller's profile. Absent for ops-generated contracts."),
        }),
    },
    // Only on a deal whose contract is signed in the lender's own system, so the
    // seller always owns it and both events track on the seller's profile.
    RECORD_FINANCING_CONTRACT_SIGNED_PENDING: {
        canonical: 'record_financing_contract_signed_pending',
        surface: 'backend',
        description: "The seller was asked to record that the buyer signed the loan contract in the lender's own system. lenderName is the display name.",
        schema: z.object({
            ...actionItemContext,
            lenderName: z
                .string()
                .optional()
                .describe('Display name of the lender whose system holds the contract, falling back to the lender id.'),
        }),
    },
    RECORD_FINANCING_CONTRACT_SIGNED_COMPLETE: {
        canonical: 'record_financing_contract_signed_complete',
        surface: 'backend',
        description: "The seller recorded the buyer's signed loan contract, or the order was funded first.",
        schema: z.object({
            ...actionItemContext,
            lenderName: z
                .string()
                .optional()
                .describe('Display name of the lender whose system holds the contract, falling back to the lender id.'),
        }),
    },
    // The lender paid out. `bankName` is on the event because the buyer-facing
    // Customer.io welcome letter is lender-specific. A journey needs an equality
    // filter on the lender it has a letter for, since a not-equals would also
    // sweep in the vehicles that have no offer at all.
    RECEIVE_LENDER_FUNDS_COMPLETE: {
        canonical: 'receive_lender_funds_complete',
        surface: 'backend',
        description: "The lender funded the buyer's loan.",
        schema: z.object({
            ...actionItemContext,
            bankName: LenderIdSchema.optional().describe('Lender that funded the loan. Absent when the vehicle has no buyer-selected offer; the generator stores that case as null, so absence means "no offer selected", never a lender.'),
        }),
    },
    // One event for every lender whose credit app is owed, including automated
    // lenders born PENDING. Customer.io posts Slack only when
    // `requiresManualSubmission` is true. A stip-free approval keeps a fallback
    // lender QUEUED, so this does not fire for that deal until the buyer declines.
    SUBMIT_CREDIT_APP_TO_LENDER_PENDING: {
        canonical: 'submit_credit_app_to_lender_pending',
        surface: 'backend',
        description: 'A credit application is owed to a lender. Ops submits by hand when requiresManualSubmission is true. Automated lenders emit the same event and do not post.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            lenderId: LenderIdSchema.describe('Lender the item is for. The action item iteration key, one item per credit-app recipient.'),
            requiresManualSubmission: z
                .boolean()
                .describe('True when the lender has no adapter, its adapter does not submit credit apps, its record switches credit app submission off, or the record could not be read. Customer.io sends the #urgent-vos Slack only when this is true.'),
            ficoScore: z
                .number()
                .optional()
                .describe('TransUnion FICO score on the credit application. Omitted when no report exists. Never the string N/A.'),
        }),
    },
    // Ends that lender's credit-app journey once the app reaches the lender, by
    // hand or through its adapter. Exit events need only the envelope and the
    // lender, so this carries none of the pending event's Slack or FICO fields.
    SUBMIT_CREDIT_APP_TO_LENDER_COMPLETE: {
        canonical: 'submit_credit_app_to_lender_complete',
        surface: 'backend',
        description: 'A credit application owed to a lender has been submitted to it.',
        schema: z.object({
            ...actionItemContext,
            lenderId: LenderIdSchema.describe('Lender the app was submitted to. A journey matches its exit on orderId and lenderId.'),
        }),
    },
    // A lender needs information from the buyer before it will take their credit
    // app. One item per lender for each round of asking, so a re-request is a
    // fresh _pending. The payload names the lender for the copy and counts what
    // was asked; the asks themselves never ride the event, since a written answer
    // can be a bank account number.
    PROVIDE_PRE_CREDIT_APP_DATA_PENDING: {
        canonical: 'provide_pre_credit_app_data_pending',
        surface: 'backend',
        description: 'A lender asked the buyer for more information before it can make an offer. One event per lender per round of asking.',
        schema: z.object({
            ...actionItemContext,
            lenderName: z
                .string()
                .optional()
                .describe('Display name of the lender asking. Absent when the lender has no saved name.'),
            requestCount: z
                .number()
                .int()
                .positive()
                .describe('How many pieces of information the lender asked for in this round.'),
        }),
    },
    // Ends the reminder journey. Fires once the buyer has answered everything in
    // the round. A round that closes because the buyer finalized with another
    // lender emits the derived _dismissed instead.
    PROVIDE_PRE_CREDIT_APP_DATA_COMPLETE: {
        canonical: 'provide_pre_credit_app_data_complete',
        surface: 'backend',
        description: 'The buyer has nothing left to answer in a round of asking from a lender.',
        schema: z.object({
            ...actionItemContext,
            lenderName: z
                .string()
                .optional()
                .describe('Display name of the lender that asked. Absent when the lender has no saved name.'),
            requestCount: z
                .number()
                .int()
                .positive()
                .describe('How many pieces of information the lender asked for in this round.'),
        }),
    },
    // Titling and registration paperwork signed remotely with a notary. The
    // mutually exclusive alternative is the mail route below; which one a buyer
    // gets is decided by their state, so the two never both fire for a vehicle.
    SCHEDULE_ROBOSIGN_SESSION_PENDING: {
        canonical: 'schedule_robosign_session_pending',
        surface: 'backend',
        description: 'Ekho asked the buyer to book a remote signing session for their title paperwork.',
        schema: z.object(actionItemContext),
    },
    SCHEDULE_ROBOSIGN_SESSION_COMPLETE: {
        canonical: 'schedule_robosign_session_complete',
        surface: 'backend',
        description: 'The buyer booked their remote signing session.',
        schema: z.object(actionItemContext),
    },
    // COMPLETE means the buyer put the signed paperwork in the mail, not that
    // Ekho has it. Receipt is `RECEIVE_WETSIG_TAR_PAPERWORK`, which is not mapped.
    MAIL_WETSIG_TAR_PAPERWORK_PENDING: {
        canonical: 'mail_wetsig_tar_paperwork_pending',
        surface: 'backend',
        description: 'Ekho asked the buyer to print, wet-sign, and mail back their title paperwork.',
        schema: z.object(actionItemContext),
    },
    MAIL_WETSIG_TAR_PAPERWORK_COMPLETE: {
        canonical: 'mail_wetsig_tar_paperwork_complete',
        surface: 'backend',
        description: 'The buyer mailed back their wet-signed title paperwork.',
        schema: z.object(actionItemContext),
    },
    // Post-delivery verification forms — inspection, VIN, smog and the like,
    // whichever the buyer's state demands. One item covers the whole set, so
    // COMPLETE means every form has been uploaded at least once, not validated.
    // A form failing validation opens `REUPLOAD_POST_FULFILLMENT_FORM` below,
    // which is per-form rather than per-set.
    UPLOAD_POST_FULFILLMENT_FORMS_PENDING: {
        canonical: 'upload_post_fulfillment_forms_pending',
        surface: 'backend',
        description: 'Ekho asked the buyer to upload their completed post-delivery forms.',
        schema: z.object(actionItemContext),
    },
    UPLOAD_POST_FULFILLMENT_FORMS_COMPLETE: {
        canonical: 'upload_post_fulfillment_forms_complete',
        surface: 'backend',
        description: 'The buyer uploaded all of their post-delivery forms.',
        schema: z.object(actionItemContext),
    },
    // The only per-form transitions in this group, hence the form identity above.
    //
    // Dedupe: once per form per iteration. A second rejection of the same form
    // opens the next iteration and re-fires this rather than introducing a
    // separate "re-rejected" verb (convention §4).
    REUPLOAD_POST_FULFILLMENT_FORM_PENDING: {
        canonical: 'reupload_post_fulfillment_form_pending',
        surface: 'backend',
        description: 'A post-delivery form failed review and Ekho asked the buyer to re-upload it.',
        schema: z.object({
            ...actionItemContext,
            ...postFulfillmentFormIdentity,
            requestedChanges: z
                .string()
                .describe("The reviewer's note explaining what to fix on this form. Always present: the generator substitutes a generic instruction when the rejected iteration carried no note, so this never arrives blank."),
        }),
    },
    // The buyer answered a specific re-upload ask. Carries the form identity but
    // not `requestedChanges`: what was wrong with the previous iteration is not
    // news once the replacement is in, and the exit only needs to know which form.
    REUPLOAD_POST_FULFILLMENT_FORM_COMPLETE: {
        canonical: 'reupload_post_fulfillment_form_complete',
        surface: 'backend',
        description: 'The buyer re-uploaded the post-delivery form that had failed review.',
        schema: z.object({
            ...actionItemContext,
            ...postFulfillmentFormIdentity,
        }),
    },
    // Reached once every uploaded form has passed validation, so PENDING here is
    // also how the buyer learns the review went their way. COMPLETE means the
    // packet is in the post, keyed off the mailed date rather than Ekho's receipt.
    SHIP_POST_FULFILLMENT_FORMS_PENDING: {
        canonical: 'ship_post_fulfillment_forms_pending',
        surface: 'backend',
        description: 'The post-delivery forms passed validation and Ekho asked the buyer to ship the packet back.',
        schema: z.object(actionItemContext),
    },
    SHIP_POST_FULFILLMENT_FORMS_COMPLETE: {
        canonical: 'ship_post_fulfillment_forms_complete',
        surface: 'backend',
        description: 'The buyer shipped their validated post-delivery packet back to Ekho.',
        schema: z.object(actionItemContext),
    },
    // Ekho-issued temp tags only, and the `EKHO_` prefix is load-bearing rather
    // than decorative. `getNotificationEvent` returns this name only for a
    // COMPLETE item that is both assigned to Ekho and resolves to Ekho's
    // services-provider id; dealer-handled titling and third-party tag providers
    // fall through to a bare `generate_temp_tag_complete`. The copy is written in
    // Ekho's voice, which is what that gate protects, so the tracker reproduces it
    // and the bare name is deliberately left undefined here — nothing consumes it.
    //
    // `registrationState` and `hasPostFulfillmentForms` are on the event for
    // branching rather than for copy: they choose the state-specific and
    // VIN-verification variants of the email.
    EKHO_GENERATE_TEMP_TAG_COMPLETE: {
        canonical: 'ekho_generate_temp_tag_complete',
        surface: 'backend',
        description: 'Ekho issued the buyer a temporary registration tag and shipped it.',
        schema: z.object({
            ...actionItemContext,
            trackingNumber: z
                .string()
                .optional()
                .describe('Carrier tracking number for the shipped tag. Absent until the tag has one: the generator omits the key rather than stamping an empty string, so absence means "not shipped yet".'),
            registrationState: z
                .string()
                .optional()
                .describe('Two-letter state the vehicle is being registered in, which selects the state-specific variant of the copy. Absent when the vehicle has no registration details recorded yet.'),
            hasPostFulfillmentForms: z
                .boolean()
                .describe('Whether the buyer still owes post-delivery forms, which selects the VIN-verification variant of the copy. Always present: the shared TAR metadata helper computes it on every generation.'),
        }),
    },
    // Ekho mailed the self-TAR documents packet to a buyer whose order was
    // converted to self-TAR. Envelope-only on the buyer's profile, matching the
    // buyer-addressed, Ekho-shipped `ekho_generate_temp_tag_complete` above. Its
    // PENDING ask is `SEND_SELF_TAR_DOCUMENTS_PENDING` in the pending cluster
    // below, so both the ask and its completion reach the CDP like the sibling
    // TAR codes. Its mailed-packet siblings (Send MCO, PA POA, TX use tax) are
    // not registered; this is the first of that family with a CDP event.
    SEND_SELF_TAR_DOCUMENTS_COMPLETE: {
        canonical: 'send_self_tar_documents_complete',
        surface: 'backend',
        description: 'Ekho mailed the buyer the documents to title their self-TAR vehicle themselves.',
        schema: z.object(actionItemContext),
    },
    // Terminal for the titling and registration side of the order: the state
    // issued the title and the plates. The survey link is per-buyer and per-order,
    // so it is composed once by the generator and carried rather than rebuilt in
    // email copy, where the contact ids would have to be interpolated by hand.
    STATE_APPROVED_TAR_PAPERWORK_COMPLETE: {
        canonical: 'state_approved_tar_paperwork_complete',
        surface: 'backend',
        description: "The state approved the vehicle's title and registration paperwork.",
        schema: z.object({
            ...actionItemContext,
            surveyUrl: z
                .string()
                .describe('Refiner post-purchase survey URL, pre-filled with the buyer id, buyer email, and order id as Refiner contact params. Always present: the generator composes it unconditionally for this item.'),
        }),
    },
    // Buyer-addressed. The action item exists for every order with a
    // post-fulfillment verification form. The tracker emits these two only when
    // a partnered vendor serves the registration county
    // (`reg31VinVerificationEligible`). Pending starts the scheduling journey.
    // Complete ends it. This item is not dismissed.
    COMPLETE_VIN_VERIFICATION_PENDING: {
        canonical: 'complete_vin_verification_pending',
        surface: 'backend',
        description: 'A fulfilled order in a partnered vendor service area still needs the buyer to schedule an at-home VIN verification.',
        schema: z.object({
            ...actionItemContext,
            ...vinVerificationScheduling,
        }),
    },
    COMPLETE_VIN_VERIFICATION_COMPLETE: {
        canonical: 'complete_vin_verification_complete',
        surface: 'backend',
        description: 'The buyer finished the at-home VIN verification a partnered vendor was offered.',
        schema: z.object({
            ...actionItemContext,
            ...vinVerificationScheduling,
        }),
    },
    // ── Dealer-addressed ──────────────────────────────────────────────────────
    //
    // Everything above is addressed to the buyer. Everything below is addressed
    // to a services provider, and is attributed to that provider's mailbox profile
    // rather than to the buyer — the audience is declared on the tracking
    // registry entry (`ACTION_ITEM_AUDIENCE.PROVIDER`) and resolved by
    // `resolveActionItemIdentity`.
    //
    // The audience is NOT inferable from the action item's `agent`, so do not
    // reach for it when adding the next one: `agent` names who must act, and four
    // of the buyer-addressed events above are `agent: EKHO`.
    //
    // These carry the same envelope as the buyer events, `buyerPortalUrl`
    // included, which is of no use to a dealer. They also carry the
    // `adminPortalUrl` a dealer does need; keeping one envelope rather than
    // forking it costs each audience a field it ignores, which is cheaper than
    // two envelopes to keep in step.
    //
    // Envelope-only except `ASSIGN_VIN_PENDING`, which carries `registrationState`
    // for T5's engine-number gate. Every other dealer field a journey needs is
    // already in the shared envelope.
    // Asked of the inventory provider (`agent: OEM`, `service: INVENTORY`).
    // The one event where `vin` is absent by definition rather than by timing:
    // assigning it is the ask.
    //
    // `registrationState` is per-event, not on the shared envelope.
    // T5 (template 242) gates the engine-number line on CA/MA/MN, and the value
    // is already stamped on every purchased-vehicle item. Putting it on
    // `actionItemContext` would widen every buyer event that has no use for it,
    // and would duplicate (or require migrating) the same field already on
    // `ekho_generate_temp_tag_complete`. Promote if a third event needs it.
    ASSIGN_VIN_PENDING: {
        canonical: 'assign_vin_pending',
        surface: 'backend',
        description: "Ekho asked the inventory provider to assign the vehicle's VIN.",
        schema: z.object({
            ...actionItemContext,
            registrationState: z
                .string()
                .optional()
                .describe('Two-letter state the vehicle is being registered in. T5 (template 242) shows the engine-number line only when this is CA, MA, or MN. Absent when the vehicle has no registration details recorded yet.'),
        }),
    },
    // The dealer places the parts-and-accessories service order, which is what
    // starts the backfill process.
    MARK_BACKFILL_ACCESSORIES_REQUESTED_PENDING: {
        canonical: 'mark_backfill_accessories_requested_pending',
        surface: 'backend',
        description: 'Ekho asked the inventory provider to place the service order for parts and accessories.',
        schema: z.object(actionItemContext),
    },
    MARK_BACKFILL_ACCESSORIES_REQUESTED_COMPLETE: {
        canonical: 'mark_backfill_accessories_requested_complete',
        surface: 'backend',
        description: 'The inventory provider placed the service order for parts and accessories.',
        schema: z.object(actionItemContext),
    },
    // Fulfillment asks addressed to the services provider assigned to the item.
    // Envelope only: the email names the order and links the portal, both of
    // which the shared envelope already carries. The schema leaves
    // `programOwnerId` and `domainOwnerSvpId` to the backend base props.
    // Dedupe: once per transition. A complete ends the journey the pending
    // event opened.
    MARK_IN_TRANSIT_TO_FULFILLMENT_CENTER_PENDING: {
        canonical: 'mark_in_transit_to_fulfillment_center_pending',
        surface: 'backend',
        description: 'Ekho asked the services provider to mark the vehicle in transit to the fulfillment center.',
        schema: z.object(actionItemContextWithoutBaseProps),
    },
    MARK_IN_TRANSIT_TO_FULFILLMENT_CENTER_COMPLETE: {
        canonical: 'mark_in_transit_to_fulfillment_center_complete',
        surface: 'backend',
        description: 'The services provider marked the vehicle in transit to the fulfillment center.',
        schema: z.object(actionItemContextWithoutBaseProps),
    },
    MARK_IN_TRANSIT_TO_BUYER_PENDING: {
        canonical: 'mark_in_transit_to_buyer_pending',
        surface: 'backend',
        description: 'Ekho asked the services provider to mark the vehicle in transit to the buyer. On white-glove orders the same transition also lands on the buyer profile with whiteGloveDealerName, so Kick Off Fulfillment white-glove emails can key off a once-per-transition action-item event instead of the racing fulfillment_status_was_updated domain event.',
        schema: z.object({
            ...actionItemContextWithoutBaseProps,
            whiteGloveDealerName: z
                .string()
                .optional()
                .describe('Last-leg provider name when last_leg_delivery is assigned and that provider is not the program owner. Present only on the buyer-profiled copy of this transition; omitted on the dealer copy and on non-white-glove orders so Customer.io can filter Stark white-glove kickoff emails on this property being set.'),
        }),
    },
    MARK_IN_TRANSIT_TO_BUYER_COMPLETE: {
        canonical: 'mark_in_transit_to_buyer_complete',
        surface: 'backend',
        description: 'The services provider marked the vehicle in transit to the buyer.',
        schema: z.object(actionItemContextWithoutBaseProps),
    },
    MARK_DELIVERY_FULFILLED_PENDING: {
        canonical: 'mark_delivery_fulfilled_pending',
        surface: 'backend',
        description: 'Ekho asked the services provider to mark the delivery fulfilled.',
        schema: z.object(actionItemContextWithoutBaseProps),
    },
    MARK_DELIVERY_FULFILLED_COMPLETE: {
        canonical: 'mark_delivery_fulfilled_complete',
        surface: 'backend',
        description: 'The services provider marked the delivery fulfilled.',
        schema: z.object(actionItemContextWithoutBaseProps),
    },
    MARK_PICKUP_SCHEDULED_PENDING: {
        canonical: 'mark_pickup_scheduled_pending',
        surface: 'backend',
        description: 'Ekho asked the services provider to mark the pickup scheduled.',
        schema: z.object(actionItemContextWithoutBaseProps),
    },
    MARK_PICKUP_SCHEDULED_COMPLETE: {
        canonical: 'mark_pickup_scheduled_complete',
        surface: 'backend',
        description: 'The services provider marked the pickup scheduled.',
        schema: z.object(actionItemContextWithoutBaseProps),
    },
    MARK_PICKUP_FULFILLED_PENDING: {
        canonical: 'mark_pickup_fulfilled_pending',
        surface: 'backend',
        description: 'Ekho asked the services provider to mark the pickup fulfilled.',
        schema: z.object(actionItemContextWithoutBaseProps),
    },
    MARK_PICKUP_FULFILLED_COMPLETE: {
        canonical: 'mark_pickup_fulfilled_complete',
        surface: 'backend',
        description: 'The services provider marked the pickup fulfilled.',
        schema: z.object(actionItemContextWithoutBaseProps),
    },
    // The counter-signer's review gate, before the doc set reaches the buyer.
    //
    // Only the dealer-facing code is registered. `resolveReviewActionCode` routes
    // to a separate `_EKHO` code whenever `servicesProviders[SALE]` is Ekho
    // itself (an Ekho-Go order), so this name by construction only ever describes
    // a review a dealer must action. The `_EKHO` variant is a Slack-only ops
    // alert with no journey and is deliberately left unregistered.
    REVIEW_DOCUMENT_SET_PURCHASE_AGREEMENT_SIGNATURES_PENDING: {
        canonical: 'review_document_set_purchase_agreement_signatures_pending',
        surface: 'backend',
        description: 'Ekho asked the counter-signing dealer to review the purchase agreement document set before it goes to the buyer for signing.',
        schema: z.object(actionItemContext),
    },
    // Wholesale Bill of Sale, transferring the vehicle from the dealer to Ekho.
    // Distinct from the buyer's `SIGN_SALE_DOCUMENT`: different document,
    // different signer, different side of the transaction.
    SIGN_WHOLESALE_BOS_PENDING: {
        canonical: 'sign_wholesale_bos_pending',
        surface: 'backend',
        description: 'Ekho asked the inventory provider to sign the wholesale Bill of Sale transferring the vehicle to Ekho.',
        schema: z.object({
            ...actionItemContext,
            sellerSignatureUrl: z.string().optional().describe(SELLER_SIGNATURE_URL_DESCRIPTION),
        }),
    },
    SIGN_WHOLESALE_BOS_COMPLETE: {
        canonical: 'sign_wholesale_bos_complete',
        surface: 'backend',
        description: 'The inventory provider signed the wholesale Bill of Sale transferring the vehicle to Ekho.',
        schema: z.object(actionItemContext),
    },
    // The COMPLETE transition, not PENDING: the notice goes out when Ekho has
    // shipped, and the PENDING ask is Ekho's own task rather than anything the
    // dealer can act on.
    //
    // Dealer-only, though it used to address the buyer too. The legacy buyer leg
    // was retired with this event's Customer.io cutover: the cutover switch is per
    // event rather than per subscriber type, so the dealer notice could not move
    // without silencing the buyer's email anyway, and ops approved a
    // single-template workflow. One emission, one profile, no discriminant needed
    // on the payload — see `NotificationTemplate`.
    SHIP_TAR_FORMS_TO_DEALER_COMPLETE: {
        canonical: 'ship_tar_forms_to_dealer_complete',
        surface: 'backend',
        description: 'Ekho shipped the signed titling and registration forms to the dealer for submission to the state.',
        schema: z.object({
            ...actionItemContext,
            trackingNumber: z
                .string()
                .optional()
                .describe('Carrier tracking number for the shipped DEALER_PACKET. Stamped from the first iteration key in tarForms.paperworkIterations.DEALER_PACKET when the action item reaches COMPLETE. Omitted when no DEALER_PACKET iteration exists yet.'),
        }),
    },
    // ── The counter-signature journey ─────────────────────────────────────────
    //
    // One dealer journey in four stages, alternating review and signature across
    // two documents: review the purchase agreement, counter-sign it, review the
    // bill of sale, counter-sign it.
    //
    // Unlike every event above, most of these exist to be *waited on* rather than
    // to send anything. The journey advances stage by stage, each step held until
    // the previous one completes, so the `_COMPLETE` events are what let it move
    // at all. An unregistered one does not drop a message — it strands the
    // journey mid-flight, silently, with no error anywhere.
    //
    // `REVIEW_DOCUMENT_SET_PURCHASE_AGREEMENT_SIGNATURES_PENDING` above opens the
    // journey and is the only one of the eight that is a trigger.
    //
    // Envelope-only, like the dealer events above. The waits match on `orderId`,
    // which the envelope already carries.
    REVIEW_DOCUMENT_SET_PURCHASE_AGREEMENT_SIGNATURES_COMPLETE: {
        canonical: 'review_document_set_purchase_agreement_signatures_complete',
        surface: 'backend',
        description: 'The counter-signing dealer approved the purchase agreement document set and it was sent for signing. Completing this review is what releases the document set to the buyer.',
        schema: z.object(actionItemContext),
    },
    // Stage two. The dealer's own signature on the agreement, once the buyer has
    // signed it.
    //
    // Legacy has a template for this but deliberately leaves providers
    // unsubscribed — "this happens before order shows up in the portal"
    // (`NotificationSubscription.ts`). No dealer is told today, on purpose. The
    // event is registered because the journey needs it to advance; whether the
    // journey should also send here is an open question for ops, since an
    // admin-portal CTA at this moment may point at an order the dealer cannot
    // yet open.
    // Drives an internal ops Slack post as well as the dealer notice, so it
    // carries the parties and the signing deadline that copy names. The audience
    // stays the dealer: this event was already emitting, and re-pointing one can
    // strand a Customer.io journey waiting on that profile.
    COUNTER_SIGN_PURCHASE_AGREEMENT_PENDING: {
        canonical: 'counter_sign_purchase_agreement_pending',
        surface: 'backend',
        description: 'The buyer signed the purchase agreement and it now awaits the dealer counter-signature.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            ...signingDeadline,
            sellerSignatureUrl: z.string().optional().describe(SELLER_SIGNATURE_URL_DESCRIPTION),
            vehiclePaymentStatus: z
                .string()
                .optional()
                .describe('Payment state of the vehicle at the moment the counter-signature was asked for, so an ops reader can see whether money has landed. Absent when the item stamped none.'),
        }),
    },
    COUNTER_SIGN_PURCHASE_AGREEMENT_COMPLETE: {
        canonical: 'counter_sign_purchase_agreement_complete',
        surface: 'backend',
        description: 'The dealer counter-signed the purchase agreement, fully executing it.',
        schema: z.object(actionItemContext),
    },
    // Stage three. The same review gate as the purchase agreement, over the bill
    // of sale document set — `REVIEW_ACTION_CODE_BY_DOC_SET_KEY` runs both off one
    // generator, so these behave identically to their PA counterparts.
    REVIEW_DOCUMENT_SET_DOCUMENTS_SIGNATURES_PENDING: {
        canonical: 'review_document_set_documents_signatures_pending',
        surface: 'backend',
        description: 'Ekho asked the counter-signing dealer to review the bill of sale document set before it goes to the buyer for signing.',
        schema: z.object(actionItemContext),
    },
    REVIEW_DOCUMENT_SET_DOCUMENTS_SIGNATURES_COMPLETE: {
        canonical: 'review_document_set_documents_signatures_complete',
        surface: 'backend',
        description: 'The counter-signing dealer approved the bill of sale document set and it was sent for signing.',
        schema: z.object(actionItemContext),
    },
    // Stage four, closing the journey. Named for the sale document rather than
    // the bill of sale: the action code is `COUNTER_SIGN_SALE_DOCUMENT`, and the
    // campaign copy calling it "bill of sale" describes the same signature.
    //
    // Distinct from `COUNTER_SIGN_WHOLESALE_BOS`, which is the dealer-to-Ekho
    // transfer rather than the sale to the buyer.
    // Same shape and the same audience reasoning as the purchase-agreement ask
    // above, minus the payment status its Slack copy does not name.
    COUNTER_SIGN_SALE_DOCUMENT_PENDING: {
        canonical: 'counter_sign_sale_document_pending',
        surface: 'backend',
        description: 'The buyer signed the sale document and it now awaits the seller counter-signature.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            ...signingDeadline,
            sellerSignatureUrl: z.string().optional().describe(SELLER_SIGNATURE_URL_DESCRIPTION),
        }),
    },
    COUNTER_SIGN_SALE_DOCUMENT_COMPLETE: {
        canonical: 'counter_sign_sale_document_complete',
        surface: 'backend',
        description: 'The seller counter-signed the sale document, fully executing it.',
        schema: z.object(actionItemContext),
    },
    // ── Internal ops Slack alerts (FOUND-2681) ────────────────────────────────
    //
    // Everything below drives an INTERNAL Slack post rather than a buyer or
    // dealer email. The backend posts these today; registering them lets a
    // Customer.io automation own the send instead, gated per event by the
    // `cio-slack-cutover` flag.
    //
    // Two things follow from the recipient being an ops channel rather than a
    // person, and both are why these entries look different from the ones above.
    // They spread `slackAlertParties`, because the message names parties the
    // reader is not, so no profile attribute can supply them. And their extra
    // fields are whatever the existing Slack template interpolates - each one was
    // traced to the generator that stamps it, so nothing here is speculative.
    //
    // `{placedOrderId}` and `{actionUrl}` in those templates deliberately get no
    // fields: the envelope already carries the same values as `orderId` and
    // `adminPortalUrl`, and the Customer.io templates bind those names instead.
    // `adminPortalUrl` matters more than a rename - it is built from stable ids,
    // so a Customer.io retry replays safely where a per-send `actionUrl` would
    // have expired.
    // The buyer signed the loan contract; Ekho counter-signs to execute it.
    // `lenderName` is display copy for the ops reader, distinct from the
    // `bankName` the funding events carry.
    COUNTER_SIGN_LOAN_CONTRACT_PENDING: {
        canonical: 'counter_sign_loan_contract_pending',
        surface: 'backend',
        description: 'The buyer signed the loan contract and it now awaits the Ekho counter-signature.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            ...signingDeadline,
            lenderName: z
                .string()
                .optional()
                .describe('Display name of the lender whose contract is awaiting counter-signature. Absent when the item was generated before a lender was selected.'),
            sellerSignatureUrl: z
                .string()
                .optional()
                .describe('Signing-vendor URL the counter-signer opens to sign. Per-send by nature and short-lived, so ops should treat it as a convenience and fall back to `adminPortalUrl`. Absent until the vendor has issued one.'),
        }),
    },
    // Ends the pending reminder on every COMPLETE transition, including when
    // payment status closes an unsigned contract. Envelope only, on the same
    // Ekho profile as the pending event, so a Customer.io wait can match orderId.
    // programOwnerId and domainOwnerSvpId stay on the backend base props.
    COUNTER_SIGN_LOAN_CONTRACT_COMPLETE: {
        canonical: 'counter_sign_loan_contract_complete',
        surface: 'backend',
        description: 'The counter-sign loan contract task closed. The seller signed, or payment status closed the item before a signature (financing guaranteed or fully paid).',
        schema: z.object(actionItemContextWithoutBaseProps),
    },
    // The buyer signed the standalone coverage packet (off-platform deals) and it
    // now awaits the Ekho seller counter-signature.
    COUNTER_SIGN_COVERAGE_CONTRACTS_PENDING: {
        canonical: 'counter_sign_coverage_contracts_pending',
        surface: 'backend',
        description: 'The buyer signed the coverage (F&I) contracts and they now await the Ekho counter-signature.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            ...signingDeadline,
            sellerSignatureUrl: z.string().optional().describe(SELLER_SIGNATURE_URL_DESCRIPTION),
        }),
    },
    // Titling paperwork, counter-signed by Ekho after the buyer signs.
    COUNTER_SIGN_TAR_PAPERWORK_PENDING: {
        canonical: 'counter_sign_tar_paperwork_pending',
        surface: 'backend',
        description: 'The buyer signed the titling paperwork and it now awaits the Ekho counter-signature.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            ...signingDeadline,
            sellerSignatureUrl: z.string().optional().describe(SELLER_SIGNATURE_URL_DESCRIPTION),
        }),
    },
    // The dealer-to-Ekho transfer, not the sale to the buyer. Ekho counter-signs
    // as the buying party here, which is the mirror of `COUNTER_SIGN_SALE_DOCUMENT`.
    COUNTER_SIGN_WHOLESALE_BOS_PENDING: {
        canonical: 'counter_sign_wholesale_bos_pending',
        surface: 'backend',
        description: 'The originating dealer signed the wholesale bill of sale and Ekho must counter-sign as the buying party.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            ...signingDeadline,
            sellerSignatureUrl: z.string().optional().describe(SELLER_SIGNATURE_URL_DESCRIPTION),
        }),
    },
    // The six document-review and fix alerts below are envelope-only, matching
    // the two already registered above (`REVIEW_DOCUMENT_SET_*_SIGNATURES_PENDING`)
    // rather than inventing a second shape for the same family. Their Slack copy
    // names no party, so they do not spread `slackAlertParties` - and the written
    // no-PII contract on those templates (`NotificationTemplate.ts`, "See SPEC §
    // No PII in Slack payloads") is a rule about the COPY, which means never
    // adding a name token to those messages even though the envelope could
    // supply one.
    REVIEW_DOCUMENT_SET_WHOLESALE_BOS_PENDING: {
        canonical: 'review_document_set_wholesale_bos_pending',
        surface: 'backend',
        description: 'A wholesale bill of sale was generated and awaits Ekho review before it goes out for signature.',
        schema: z.object(actionItemContext),
    },
    // The Ekho-counter-signed variants of the two dealer review events. The
    // `_EKHO` action code is a separate item, not a flag on the dealer one, which
    // is why it gets its own canonical.
    REVIEW_DOCUMENT_SET_DOCUMENTS_SIGNATURES_EKHO_PENDING: {
        canonical: 'review_document_set_documents_signatures_ekho_pending',
        surface: 'backend',
        description: 'A bill of sale document set awaits Ekho review on an order Ekho counter-signs itself, so no dealer is asked.',
        schema: z.object(actionItemContext),
    },
    REVIEW_DOCUMENT_SET_PURCHASE_AGREEMENT_SIGNATURES_EKHO_PENDING: {
        canonical: 'review_document_set_purchase_agreement_signatures_ekho_pending',
        surface: 'backend',
        description: 'A purchase agreement document set awaits Ekho review on an order Ekho counter-signs itself, so no dealer is asked.',
        schema: z.object(actionItemContext),
    },
    // A reviewer rejected the set; ops has to regenerate or correct it. The
    // rejection note is deliberately not carried: the template does not render it,
    // and it is free-text an operator wrote about a named buyer.
    FIX_DOCUMENT_SET_DOCUMENTS_SIGNATURES_PENDING: {
        canonical: 'fix_document_set_documents_signatures_pending',
        surface: 'backend',
        description: 'A reviewer rejected the bill of sale document set and ops must correct it.',
        schema: z.object(actionItemContext),
    },
    FIX_DOCUMENT_SET_PURCHASE_AGREEMENT_SIGNATURES_PENDING: {
        canonical: 'fix_document_set_purchase_agreement_signatures_pending',
        surface: 'backend',
        description: 'A reviewer rejected the purchase agreement document set and ops must correct it.',
        schema: z.object(actionItemContext),
    },
    FIX_DOCUMENT_SET_WHOLESALE_BOS_PENDING: {
        canonical: 'fix_document_set_wholesale_bos_pending',
        surface: 'backend',
        description: 'Ekho rejected the wholesale bill of sale and ops must correct it.',
        schema: z.object(actionItemContext),
    },
    // The titling paperwork is signature-ready and unsigned. Envelope-only and
    // buyer-audienced: the notification template is null, so this exists for a
    // Customer.io nudge to key the e-sign ask off the order and portal link,
    // matching the SUBMIT_INSURANCE_INFORMATION and
    // SUBMIT_FINANCING_APPLICATION_TO_EKHO asks.
    ESIGN_TAR_PAPERWORK_PENDING: {
        canonical: 'esign_tar_paperwork_pending',
        surface: 'backend',
        description: 'The titling paperwork is ready for the buyer to e-sign.',
        schema: z.object(actionItemContext),
    },
    // The buyer finished e-signing the titling packet. `sellerSignatureUrl` is
    // newly stamped by the generator in this change: the counter-sign sibling
    // stamped it and this one did not, so the existing Slack message renders a
    // literal "N/A" today.
    ESIGN_TAR_PAPERWORK_COMPLETE: {
        canonical: 'esign_tar_paperwork_complete',
        surface: 'backend',
        description: 'The buyer finished e-signing the titling paperwork.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            ...signingDeadline,
            sellerSignatureUrl: z
                .string()
                .optional()
                .describe('Signing-vendor URL for the seller signature on the same document set. Absent until the vendor has issued one.'),
        }),
    },
    // Ekho's own task, on a dealer-titling order: mail the signed originals to
    // the dealer who will file them. Addressed to that dealer rather than to the
    // assignee, which the identity resolver already special-cases.
    SHIP_TAR_FORMS_TO_DEALER_PENDING: {
        canonical: 'ship_tar_forms_to_dealer_pending',
        surface: 'backend',
        description: 'The buyer signed their titling paperwork, so Ekho must ship the signed originals to the dealer who files them.',
        schema: z.object({ ...actionItemContext, ...slackAlertParties }),
    },
    // Ekho's own task on a self-TAR order: the buyer is owed the packet of
    // documents they need to title the vehicle themselves, not yet generated and
    // mailed. Buyer-addressed and envelope-only, matching its COMPLETE above. The
    // null template sends nothing; the event opens the CDP record so a Customer.io
    // journey can key the ask, and the derived `_dismissed` ends that journey.
    SEND_SELF_TAR_DOCUMENTS_PENDING: {
        canonical: 'send_self_tar_documents_pending',
        surface: 'backend',
        description: 'Ekho still owes the buyer the documents to title their self-TAR vehicle themselves.',
        schema: z.object(actionItemContext),
    },
    // The urgent variant only. `GENERATE_TEMP_TAG_PENDING` has a null template,
    // and `getNotificationEvent` picks this name only when the item is flagged
    // urgent - which is set when the vehicle is already FULFILLED with the tag
    // still outstanding. The tracking entry keys the plain PENDING transition and
    // declines when the item is not urgent, because the transition key carries no
    // urgency dimension.
    GENERATE_TEMP_TAG_PENDING_URGENT: {
        canonical: 'generate_temp_tag_pending_urgent',
        surface: 'backend',
        description: 'A temp tag is still outstanding on a vehicle that has already been marked fulfilled, so the ask is urgent.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            vehicleFulfillmentStatus: z
                .enum(VEHICLE_FULFILLMENT_STATUS_VALUES)
                .optional()
                .describe('Fulfillment status of the vehicle at the moment the ask became urgent. In practice `FULFILLED`, which is the condition that sets the urgent flag; typed as the full enum rather than a literal so a future urgency rule does not silently violate the schema.'),
        }),
    },
    // A VIN was recorded against the vehicle. Nothing sourced `daysSincePurchase`
    // before, so the existing Slack message renders a literal "N/A" today.
    ASSIGN_VIN_COMPLETE: {
        canonical: 'assign_vin_complete',
        surface: 'backend',
        description: 'A VIN was recorded against the purchased vehicle.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            daysSincePurchase: z
                .number()
                .optional()
                .describe('Days since the vehicle was purchased, so ops can see how long the unit went unidentified. Resolved from the vehicle when the event is emitted rather than stamped into action-item metadata, because the item is written when the task is created and this fires when the VIN is finally assigned, so a persisted value would report the age at task creation instead. Absent when the vehicle carries no purchase date or could not be read.'),
        }),
    },
    // A buyer-uploaded post-delivery form is waiting on an ops validator.
    VALIDATE_POST_FULFILLMENT_FORM_PENDING: {
        canonical: 'validate_post_fulfillment_form_pending',
        surface: 'backend',
        description: 'A buyer uploaded a post-delivery form and it awaits ops validation.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            ...postFulfillmentFormIdentity,
        }),
    },
    // The order needs a unit backfilled into the inventory system and the request
    // has not been marked sent. The model fields identify what to order.
    MARK_BACKFILL_INVENTORY_REQUESTED_PENDING: {
        canonical: 'mark_backfill_inventory_requested_pending',
        surface: 'backend',
        description: 'The order needs an inventory backfill and nobody has marked the request as sent yet.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            modelName: z
                .string()
                .optional()
                .describe('Model name of the unit to backfill. Absent when the item stamped none.'),
            modelYear: z
                .number()
                .optional()
                .describe('Model year of the unit to backfill. Absent when the item stamped none.'),
            productId: z
                .string()
                .optional()
                .describe('Catalog product id of the unit to backfill, for matching against the inventory system. Absent when the item stamped none.'),
        }),
    },
    // Order-scoped rather than vehicle-scoped, so the vehicle fields resolve only
    // when the order has exactly one vehicle. A multi-vehicle order omits them.
    REVIEW_RISK_ASSESSMENT_PENDING: {
        canonical: 'review_risk_assessment_pending',
        surface: 'backend',
        description: 'An order risk assessment entered review and needs an Ekho reviewer.',
        schema: z.object({ ...placedOrderScopedContext, ...slackAlertParties }),
    },
    // A lender approval was intercepted because it failed the promotional APR
    // floor check, so ops has to confirm the promotional terms (Record lender
    // decision) or dismiss the guard. Internal ops Slack alert on the Ekho
    // profile, which the Customer.io automation routes to #urgent-vos. Fires once
    // when the item turns PENDING; the derived `_dismissed` ends the journey.
    //
    // Vehicle-scoped: the generator runs per purchased vehicle, so the shared
    // metadata stamps `purchasedVehicleId`, `vehicleName`, and the order. The
    // lender is carried by the envelope's `bankName`/`lenderId`.
    CONFIRM_FINANCING_PROMOTION_PENDING: {
        canonical: 'confirm_financing_promotion_pending',
        surface: 'backend',
        description: 'A lender approval was intercepted because it failed the promotional APR floor check, so ops must confirm the promotional terms or dismiss the guard.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            reason: z
                .string()
                .describe('Why the approval was intercepted, in plain words: the approved APR exceeded the promotional floor of the financing promotion the deal applied. Always present.'),
            approvedApr: z
                .number()
                .optional()
                .describe('APR the lender approved, as a decimal (0.0799 = 7.99%). Absent only on a legacy intercept that stamped none.'),
            aprFloor: z
                .number()
                .optional()
                .describe('Promotional APR floor of the financing promotion the deal applied, as a decimal. Absent when the intercept carries no floor.'),
            ficoScore: z
                .number()
                .optional()
                .describe('TransUnion soft-pull FICO on the deal, for context. The floor check compares APR only and does not use it. Absent when no credit score was available.'),
            promoName: z
                .string()
                .optional()
                .describe('Display name of the financing promotion the deal applied, so the alert names which promo the deal missed. Absent when the intercept carries no floor.'),
            promoId: z
                .string()
                .optional()
                .describe('Id of the financing promotion the deal applied. Absent when the intercept carries no floor.'),
        }),
    },
    // The buyer finished signing every financing document; the packet is not yet
    // with the lender. Sent on every deal.
    SUBMIT_FUNDING_PACKET_PENDING: {
        canonical: 'submit_funding_packet_pending',
        surface: 'backend',
        description: 'The buyer finished signing their financing documents and the funding packet has not gone to the lender yet.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            buyerId: z
                .string()
                .optional()
                .describe('Buyer id, carried so an ops reader can jump straight to the buyer record. Absent when the item stamped none.'),
            bankName: z
                .string()
                .optional()
                .describe('Lender the packet is going to. Absent when the item was generated before a lender was selected.'),
            lenderInteractionOwner: z
                .string()
                .describe("Services provider id of whoever works the lender on this deal: Ekho's id when Ekho can work the lender's portal, the deal's program owner otherwise. Customer.io can route the ops alert on it."),
            lenderId: z
                .string()
                .optional()
                .describe('Id of the lender the packet goes to. Absent when the item stamped none.'),
            lenderName: z
                .string()
                .optional()
                .describe('Display name of the lender the packet goes to. Absent when the item stamped none.'),
        }),
    },
    RECORD_FUNDING_APPROVAL_PENDING: {
        canonical: 'record_funding_approval_pending',
        surface: 'backend',
        description: 'The funding packet went to the lender, so the seller can record its funding approval. Sent on every financed deal.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            ...recordFundingApprovalProps,
        }),
    },
    RECORD_FUNDING_APPROVAL_COMPLETE: {
        canonical: 'record_funding_approval_complete',
        surface: 'backend',
        description: 'The lender approved funding on the deal, recorded by the seller, the lender itself, or a retrigger. Sent on every financed deal.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            ...recordFundingApprovalProps,
            fundingSource: z
                .nativeEnum(COMPLETION_SOURCE)
                .optional()
                .describe('The flow that reported the deal funded: ACTION_ITEM (the seller, by hand), RETRIGGER (by hand, from the retrigger tool), EMAIL_PARSING or API (the lender). Absent on a deal funded before the source was recorded.'),
        }),
    },
    // Sent on every deal.
    SUBMIT_FUNDING_PACKET_COMPLETE: {
        canonical: 'submit_funding_packet_complete',
        surface: 'backend',
        description: 'The funding packet was submitted to the lender.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            lenderInteractionOwner: z
                .string()
                .describe("Services provider id of whoever works the lender on this deal: Ekho's id when Ekho can work the lender's portal, the deal's program owner otherwise. Customer.io can route the ops alert on it."),
            lenderId: z
                .string()
                .optional()
                .describe('Id of the lender the packet goes to. Absent when the item stamped none.'),
            lenderName: z
                .string()
                .optional()
                .describe('Display name of the lender the packet went to. Absent when the item stamped none.'),
        }),
    },
    // The buyer turned down an approved offer. The two maps say what they were
    // turning down, which is what makes the alert worth acting on.
    DECLINE_FINANCING_OFFER_COMPLETE: {
        canonical: 'decline_financing_offer_complete',
        surface: 'backend',
        description: 'The buyer declined an approved financing offer.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            declinedAprsByBankName: z
                .string()
                .optional()
                .describe('The declined APRs, keyed by lender, as the generator rendered them for display. Display copy rather than structured data - read it, do not filter on it. Absent when the item stamped none.'),
            declinedDownPaymentsByBankName: z
                .string()
                .optional()
                .describe('The declined down payments, keyed by lender, as the generator rendered them for display. Display copy rather than structured data. Absent when the item stamped none.'),
        }),
    },
    // A valid insurance policy is sitting unreviewed. The COMPLETE twin is
    // already registered above; this is the ask rather than the outcome.
    REVIEW_INSURANCE_INFORMATION_PENDING: {
        canonical: 'review_insurance_information_pending',
        surface: 'backend',
        description: 'The buyer submitted a valid insurance policy that ops has not reviewed yet.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            carrierName: z
                .string()
                .optional()
                .describe('Insurance carrier named on the submitted policy. Absent when the policy fields did not parse.'),
            carrierPolicyNumber: z
                .string()
                .optional()
                .describe('Policy number on the submitted document, so a reviewer can match it against the carrier. Absent when the policy fields did not parse.'),
        }),
    },
    // Ops reviewed the policy and now has to call the carrier to confirm it. Only
    // dealer-seller orders get this item. Same payload as the review ask, because
    // the Slack copy names the same carrier and policy number.
    VERIFY_INSURANCE_WITH_PROVIDER_PENDING: {
        canonical: 'verify_insurance_with_provider_pending',
        surface: 'backend',
        description: 'Ops reviewed the buyer insurance policy and has to call the carrier to verify it.',
        schema: z.object({
            ...actionItemContext,
            ...slackAlertParties,
            carrierName: z
                .string()
                .optional()
                .describe('Insurance carrier named on the reviewed policy, the one ops calls. Absent when the policy fields did not parse.'),
            carrierPolicyNumber: z
                .string()
                .optional()
                .describe('Policy number to quote to the carrier on the verification call. Absent when the policy fields did not parse.'),
        }),
    },
    // Ops confirmed the policy with the carrier. No Ekho notification sends on
    // this transition, but a Customer.io journey keyed on the PENDING ask exits
    // on it. Envelope-only. Dedupe: once per insurance cycle, on a genuine
    // COMPLETE, never on born-complete history.
    VERIFY_INSURANCE_WITH_PROVIDER_COMPLETE: {
        canonical: 'verify_insurance_with_provider_complete',
        surface: 'backend',
        description: 'Ops verified the buyer insurance policy with the carrier.',
        schema: z.object(actionItemContext),
    },
    // Every lender on a deal that allows a lender attach rejected the
    // buyer, and the seller has to attach another lender. Addressed to the
    // seller. Envelope-only: the message names the order, and the admin-portal
    // link opens it. Dedupe: once per round, since each round is its own item.
    ATTACH_NEW_LENDER_PENDING: {
        canonical: 'attach_new_lender_pending',
        surface: 'backend',
        description: 'Every lender on the credit application rejected the buyer, and the seller has to attach another lender.',
        schema: z.object(actionItemContext),
    },
    // The buyer reached the point a native buyer signs the credit app, on an
    // external lender that needs the seller to confirm that step outside Ekho.
    // Addressed to the seller. Envelope-only: the message names the order, and
    // the admin-portal link opens the item. Fires each time the item goes
    // pending: once per deal, unless a revised decision reopens a stip first.
    CONFIRM_BUYER_COMMITTED_TO_LENDER_PENDING: {
        canonical: 'confirm_buyer_committed_to_lender_pending',
        surface: 'backend',
        description: "The buyer reached the external lender's commitment step, and the seller has to confirm the buyer is committed to that lender.",
        schema: z.object(actionItemContext),
    },
    // Sales-agent inbox. A rep owes the dealership's answer, number, file, or
    // reply, and the sales agent cannot produce it. Addressed to the owning
    // services provider. Dedupe: once per request (iteration key is the request
    // id) for the three request items, and once per conversation for the reply
    // item until a newer buyer message reopens it. The complete twin ends the
    // journey; dismissed is derived from pending.
    ANSWER_QUESTION_PENDING: {
        canonical: 'answer_question_pending',
        surface: 'backend',
        description: 'The AI Sales Agent asked the dealership a question it cannot answer, and a rep has not answered yet.',
        schema: agentRequestEventSchema,
    },
    ANSWER_QUESTION_COMPLETE: {
        canonical: 'answer_question_complete',
        surface: 'backend',
        description: 'A rep answered the question the AI Sales Agent asked, so the agent can reply to the buyer.',
        schema: agentRequestEventSchema,
    },
    PROVIDE_VALUE_PENDING: {
        canonical: 'provide_value_pending',
        surface: 'backend',
        description: 'The AI Sales Agent needs a number from the dealership before it can answer a buyer.',
        schema: agentRequestEventSchema,
    },
    PROVIDE_VALUE_COMPLETE: {
        canonical: 'provide_value_complete',
        surface: 'backend',
        description: 'A rep supplied the number the AI Sales Agent asked for.',
        schema: agentRequestEventSchema,
    },
    SUBMIT_MEDIA_PENDING: {
        canonical: 'submit_media_pending',
        surface: 'backend',
        description: 'The AI Sales Agent needs a photo, video, or document from the dealership before it can answer a buyer.',
        schema: agentRequestEventSchema,
    },
    SUBMIT_MEDIA_COMPLETE: {
        canonical: 'submit_media_complete',
        surface: 'backend',
        description: 'A rep uploaded the file the AI Sales Agent asked for.',
        schema: agentRequestEventSchema,
    },
    REPLY_TO_CONVERSATION_PENDING: {
        canonical: 'reply_to_conversation_pending',
        surface: 'backend',
        description: 'Auto-reply is paused and a buyer message is waiting on a person at the dealership.',
        schema: agentRequestEventSchema,
    },
    REPLY_TO_CONVERSATION_COMPLETE: {
        canonical: 'reply_to_conversation_complete',
        surface: 'backend',
        description: 'A rep finished the reply a paused conversation was waiting on.',
        schema: agentRequestEventSchema,
    },
};
/**
 * One `{actionCode}_dismissed` event for every action item with a registered
 * `_pending` transition: those are the items with a Customer.io journey, and a
 * journey ends on `_complete` or on this. The tracking route sends it to the
 * same profile with the same payload as the `_pending` event, so the schema is
 * the `_pending` one.
 *
 * Derived rather than written per code, so an item registered later gets its
 * dismissed event with no further entry. Kept out of `ACTION_ITEM_EVENTS`
 * because Ekho sends no dismissal notification, so these names have no
 * `NotificationEvent` counterpart, which every `ACTION_ITEM_EVENTS` entry must.
 * Dedupe: once per transition to DISMISSED. An item created already dismissed
 * started no journey and sends nothing.
 */
const ACTION_ITEM_DISMISSED_EVENTS = Object.fromEntries(Object.entries(ACTION_ITEM_EVENTS)
    .filter(([key]) => key.endsWith(PENDING_KEY_SUFFIX))
    .map(([key, pending]) => {
    const dismissedKey = `${key.slice(0, -PENDING_KEY_SUFFIX.length)}${DISMISSED_KEY_SUFFIX}`;
    const definition = {
        canonical: dismissedKey.toLowerCase(),
        surface: pending.surface,
        description: `The item closed without the work being done, so its journey ends. It opened as: ${pending.description}`,
        schema: pending.schema,
    };
    return [dismissedKey, definition];
}));

export { ACTION_ITEM_DISMISSED_EVENTS, ACTION_ITEM_EVENTS };
//# sourceMappingURL=action-items.js.map
